import { getLinkAriaLabel, getUrlText } from '@/utils/urlUtils';

import { AspectRatioImage } from '@/components/AspectRatioImage/AspectRatioImage';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { handleSubmenuArrowNavigation } from '@/components/Megamenu/helpers';
import type { ContentfulMainMenuItemFragment } from '@/graphql/contentful/api';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as S from './Collections.styles';

export interface CollectionsProps {
  collections: ContentfulMainMenuItemFragment;
  setActiveSubmenu: (isActive: string) => void;
}

export const Collections = ({
  collections,
  setActiveSubmenu,
}: CollectionsProps) => {
  const { locale } = useRegionProvider();
  return (
    <S.Box data-test-id='CollectionsBox'>
      <S.DesktopMegamenu data-test-id='CollectionsDesktopMegamenu' role='menu'>
        <S.Collections data-test-id='CollectionsCollections'>
          {collections?.submenuItemsCollection?.items?.map(
            (collection, index) => (
              <S.CollectionLink
                key={'collection' + collection?.internalName}
                aria-label={getLinkAriaLabel(
                  getUrlText(collection?.url ?? ''),
                  locale.code,
                )}
                data-test-id={`CollectionsCollection-${index}`}
                href={getUrlText(collection?.url ?? '')}
                onClick={() => setActiveSubmenu('')}
                onKeyDown={(e) =>
                  handleSubmenuArrowNavigation(e, {
                    nextKey: 'ArrowRight',
                    prevKey: 'ArrowLeft',
                  })
                }
                role='menuitem'
                target={collection?.openUrlInNewTab ? '_blank' : '_self'}
              >
                <S.CollectionItem
                  data-test-id={`CollectionsCollectionItem-${index}`}
                >
                  <AspectRatioImage
                    alt={`collection - ${collection?.image?.title}`}
                    aspectRatio={{
                      x: collection?.image?.width ?? 1,
                      y: collection?.image?.height ?? 1,
                    }}
                    className='image'
                    data-test-id={`CollectionsCollectionItemImage-${index}`}
                    focalPoint={{
                      x: (collection?.image?.width ?? 0) / 2,
                      y: (collection?.image?.height ?? 0) / 2,
                    }}
                    loaderProps={{ height: 175 }}
                    loading='lazy'
                    originalImageDimensions={{
                      x: collection?.image?.width ?? 0,
                      y: collection?.image?.height ?? 0,
                    }}
                    showFallbackPlaceholder
                    sizes='10vw'
                    src={collection?.image?.url ?? ''}
                    useLoader
                    width='100%'
                  />
                  <S.CollectionTitle
                    data-test-id={`CollectionsCollectionTitle-${index}`}
                  >
                    {collection?.label}
                  </S.CollectionTitle>
                </S.CollectionItem>
              </S.CollectionLink>
            ),
          )}
        </S.Collections>
        {collections?.bottomLinkLabel !== '' && (
          <S.LinkBox data-test-id='CollectionsLinkBox'>
            <LinkWithLocale
              aria-label={getLinkAriaLabel(
                collections?.bottomLinkUrl ?? '',
                locale.code,
              )}
              href={collections?.bottomLinkUrl ?? ''}
              onClick={() => setActiveSubmenu('')}
              onKeyDown={(e) =>
                handleSubmenuArrowNavigation(e, {
                  nextKey: 'ArrowRight',
                  prevKey: 'ArrowLeft',
                })
              }
              role='menuitem'
            >
              {collections?.bottomLinkLabel}
            </LinkWithLocale>
          </S.LinkBox>
        )}
      </S.DesktopMegamenu>
    </S.Box>
  );
};

Collections.displayName = 'Collections';
